import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  Wrench,
  Home,
  Plug,
  Hammer,
  Zap,
  LineChart,
  UsersRound,
  Settings as SettingsIcon,
  BookOpen,
  LifeBuoy,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useStore } from "../stores/store";
import { cn } from "../utils/cn";
import { SidebarState } from "../stores/global.store";
import { useAuth } from "src/contexts/AuthContext";
import { useNavigation } from "../hooks/useNavigation";
import { usePermission } from "../hooks/usePermission";
import type {
  NavItem,
  NavGroup,
  NavSubgroup,
  ModuleStatus,
} from "@/modules/types";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "./ui/collapsible";

// Initialize module registry on first import
import "@/modules";
import ReactDOM from "react-dom";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";

/**
 * Per-group icons rendered next to the section header.
 *
 * The previous code hardcoded a `Users` icon on the collapsible header path
 * and showed nothing on the flat header path — that's been replaced with this
 * single map so every group gets a meaningful, consistent icon.
 */
const GROUP_ICONS: Record<string, LucideIcon> = {
  overview: Home,
  connect: Plug,
  build: Hammer,
  run: Zap,
  observe: LineChart,
  team: UsersRound,
  account: SettingsIcon,
};

// Status Badge Component
interface StatusBadgeProps {
  status: ModuleStatus;
  isCollapsed: boolean;
  hasNotification?: boolean;
}

/**
 * Determines whether internal roadmap badges ("Dev", "Soon") should be
 * visible. These pills leak our internal release state to tenants, so in
 * production we only show them to elevated users (admins / "manage all").
 * Outside of production they always render to keep dev/staging useful.
 */
const useShowRoadmapBadges = (): boolean => {
  const { can, isLoading } = usePermission();
  const isProduction = import.meta.env.MODE === "production";
  if (!isProduction) return true;
  if (isLoading) return false;
  return can("manage", "all");
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  isCollapsed,
  hasNotification,
}) => {
  const { t } = useTranslation("modules");
  const showRoadmapBadges = useShowRoadmapBadges();
  // Show notification badge if present
  if (hasNotification) {
    if (isCollapsed) {
      return (
        <div
          className={cn(
            "absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-white",
            "bg-red-500 shadow-md animate-pulse",
          )}
        />
      );
    }
    return (
      <span
        className={cn(
          "ml-auto px-2 py-1 text-xs font-bold rounded-full",
          "bg-red-500 text-white shadow-md animate-pulse",
        )}
      >
        ●
      </span>
    );
  }

  if (status === "active") return null;

  // Hide internal roadmap pills from tenants in production builds —
  // only elevated users (admin / "manage all") see them.
  if (!showRoadmapBadges) return null;

  const badgeConfig = {
    "in-development": {
      icon: Wrench,
      text: t("nav.status.in-development", "Dev"),
      className: "bg-amber-100 text-amber-700 border-amber-200",
    },
    "coming-soon": {
      icon: Clock,
      text: t("nav.status.coming-soon", "Soon"),
      className: "bg-blue-100 text-blue-700 border-blue-200",
    },
  };

  const config = badgeConfig[status];
  const Icon = config.icon;

  if (isCollapsed) {
    return (
      <div
        className={cn(
          "absolute -top-1 -right-1 h-3 w-3 rounded-full border",
          config.className,
        )}
      >
        <Icon className="h-2 w-2 absolute top-0.5 left-0.5" />
      </div>
    );
  }

  return (
    <span
      className={cn(
        "ml-auto px-1.5 py-0.5 text-xs font-medium rounded-md border flex items-center gap-1",
        config.className,
      )}
    >
      <Icon className="h-3 w-3" />
      {config.text}
    </span>
  );
};

// Sidebar Item Component
interface SidebarItemProps {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  isActive,
  isCollapsed,
}) => {
  const { i18n } = useTranslation();
  const { globalStore } = useStore(); // Add this to access globalStore
  const { isMobileView, setSidebarState } = globalStore; // Get mobile state and setter
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
  const itemRef = React.useRef<HTMLDivElement>(null);
  const Icon = item.icon;

  // Detect if current language is RTL (Arabic)
  const isRTL = i18n.language.startsWith("ar");

  const updateTooltipPosition = () => {
    if (itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      setTooltipPosition({ top: rect.top + rect.height / 2 });
    }
  };

  const isDisabled = item.disabled;

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault();
    }

    // On mobile, close the sidebar after navigation
    if (isMobileView && !isDisabled) {
      // Small delay to allow navigation to happen first
      setTimeout(() => {
        setSidebarState(SidebarState.COLLAPSED);
      }, 100);
    }
  };

  const itemContent = (
    <div
      className={cn(
        "flex items-center text-sm font-medium rounded-xl relative overflow-hidden",
        "transform transition-all duration-300 ease-out",
        "before:absolute before:inset-0 before:bg-gradient-to-r before:from-blue-50/80 before:to-primary-50/80",
        "before:translate-x-[-100%] before:transition-transform before:duration-300 before:ease-out",
        !isDisabled &&
        "hover:before:translate-x-0 hover:scale-[1.01] hover:shadow-lg hover:shadow-blue-100/30",
        !isDisabled &&
        "active:scale-[0.99] active:transition-transform active:duration-100",
        isActive
          ? "bg-gradient-to-r from-blue-50 to-primary-50 text-blue-700 shadow-md shadow-blue-100/40 border border-blue-200/30"
          : isDisabled
            ? "text-gray-400 cursor-not-allowed opacity-60"
            : "text-gray-600 hover:text-gray-900",
        isCollapsed
          ? "justify-center p-2.5 mx-auto w-[52px] h-[44px]"
          : "px-3 py-2.5",
        isRTL && "text-right",
      )}
      onMouseEnter={() => {
        setIsHovered(true);
        if (isCollapsed) {
          updateTooltipPosition();
          setShowTooltipState(true);
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowTooltipState(false);
      }}
      onClick={handleClick}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-r from-white/60 to-white/40",
          "opacity-0 transition-opacity duration-300 ease-out",
          "rounded-xl",
          !isActive && isHovered && !isDisabled && "opacity-100",
        )}
      />

      <div
        className={cn(
          "relative z-10 flex items-center",
          isCollapsed ? "justify-center w-full" : "w-full",
          isRTL && "flex-row-reverse",
        )}
      >
        <div className="relative">
          <Icon
            className={cn(
              "flex-shrink-0 transition-all duration-300 ease-out",
              isCollapsed ? "h-6 w-6" : "h-5 w-5",
              isActive
                ? "text-blue-600 stroke-[2.5]"
                : isDisabled
                  ? "text-gray-400 stroke-2"
                  : "text-gray-500 stroke-2",
              isHovered &&
              !isActive &&
              !isDisabled &&
              "text-gray-700 stroke-[2.2]",
              isHovered &&
              !isActive &&
              !isDisabled &&
              !isCollapsed &&
              "scale-110",
              isHovered &&
              !isActive &&
              !isDisabled &&
              isCollapsed &&
              "scale-105",
              !isCollapsed && (isRTL ? "ml-3" : "mr-3"),
            )}
          />
          {isCollapsed && (
            <StatusBadge
              status={item.status}
              isCollapsed={true}
              hasNotification={item.hasNotification}
            />
          )}
        </div>

        {!isCollapsed && (
          <>
            <span
              className={cn(
                "truncate transition-all duration-300 ease-out flex-1",
                isActive ? "font-semibold" : "font-medium",
                isHovered && !isActive && !isDisabled && "font-semibold",
                isHovered &&
                !isActive &&
                !isDisabled &&
                (isRTL ? "-translate-x-1" : "translate-x-1"),
              )}
            >
              {item.label}
            </span>
            <StatusBadge
              status={item.status}
              isCollapsed={false}
              hasNotification={item.hasNotification}
            />
          </>
        )}
      </div>

      <div
        className={cn(
          "absolute top-1/2 -translate-y-1/2 rounded-r-full",
          "bg-gradient-to-b from-blue-500 to-primary-500",
          "transition-all duration-300 ease-out",
          isCollapsed ? "w-1" : "w-1",
          isRTL ? "right-0 rounded-l-full" : "left-0 rounded-r-full",
          isActive
            ? isCollapsed
              ? "h-6 opacity-100"
              : "h-8 opacity-100"
            : "h-0 opacity-0",
          isHovered &&
          !isActive &&
          !isDisabled &&
          (isCollapsed ? "h-3 opacity-60" : "h-4 opacity-60"),
        )}
      />

      <div
        className={cn(
          "absolute inset-0 rounded-xl transition-opacity duration-300",
          "bg-gradient-to-r from-blue-400/5 to-primary-400/5",
          isHovered && !isActive && !isDisabled ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );

  return (
    <div className="relative" ref={itemRef} data-tour={`sidebar-item-${item.id}`}>
      {isDisabled ? (
        <div>{itemContent}</div>
      ) : (
        <Link to={item.to}>{itemContent}</Link>
      )}

      {isCollapsed &&
        showTooltipState &&
        typeof window !== "undefined" &&
        ReactDOM.createPortal(
          <div
            className="fixed pointer-events-none"
            style={{
              left: isRTL ? "auto" : "72px",
              right: isRTL ? "72px" : "auto",
              top: tooltipPosition.top,
              transform: "translateY(-50%)",
              zIndex: 2147483647, // Maximum z-index
            }}
          >
            <div
              className={cn(
                "px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap",
                "shadow-lg shadow-black/30",
              )}
            >
              <div className="font-medium">{item.label}</div>
              {item.description && (
                <div className="text-xs text-gray-300 mt-1 max-w-xs">
                  {item.description}
                </div>
              )}
              {item.status !== "active" && (
                <div className="text-xs text-amber-300 mt-1 flex items-center gap-1">
                  {item.status === "in-development" && (
                    <Wrench className="h-3 w-3" />
                  )}
                  {item.status === "coming-soon" && (
                    <Clock className="h-3 w-3" />
                  )}
                  {item.status === "in-development"
                    ? "In Development"
                    : "Coming Soon"}
                </div>
              )}
              <div
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 border-[6px] border-transparent",
                  isRTL
                    ? "left-full border-l-slate-900"
                    : "right-full border-r-slate-900",
                )}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// Sidebar Subgroup Component (collapsible nested section)
interface SidebarSubgroupProps {
  subgroup: NavSubgroup;
  isRouteActive: (path: string) => boolean;
  isRTL: boolean;
}

const SidebarSubgroupComponent: React.FC<SidebarSubgroupProps> = ({
  subgroup,
  isRouteActive,
  isRTL,
}) => {
  const hasActiveChild = subgroup.items.some(item => isRouteActive(item.to));
  const storageKey = `sidebar:cdp:collapsed:${subgroup.id}`;

  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) return stored === "open";
    return hasActiveChild;
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    localStorage.setItem(storageKey, open ? "open" : "closed");
  };

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
      <CollapsibleTrigger className="w-full">
        <div
          className={cn(
            "flex items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase text-gray-400 tracking-wider",
            "hover:text-gray-600 transition-colors duration-200 cursor-pointer rounded-lg hover:bg-gray-50",
            isRTL && "flex-row-reverse text-right",
          )}
        >
          <span>{subgroup.title}</span>
          <ChevronRight
            className={cn(
              "h-3 w-3 text-gray-400 transition-transform duration-200",
              isOpen && "rotate-90",
              !isOpen && isRTL && "rotate-180",
            )}
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className={cn("space-y-1 mt-1", isRTL ? "pr-2" : "pl-2")}>
          {subgroup.items.map(item => (
            <SidebarItem
              key={item.id}
              item={item}
              isActive={isRouteActive(item.to)}
              isCollapsed={false}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Collapsible Group Content (extracted to use useState for chevron rotation)
interface CollapsibleGroupContentProps {
  group: NavGroup;
  allGroupItems: NavItem[];
  defaultOpen: boolean;
  isRouteActive: (path: string) => boolean;
  isRTL: boolean;
}

const CollapsibleGroupContent: React.FC<CollapsibleGroupContentProps> = ({
  group,
  allGroupItems,
  defaultOpen,
  isRouteActive,
  isRTL,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-8">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full">
          <div
            className={cn(
              "mb-2 px-3 flex items-center justify-between cursor-pointer group",
              isRTL && "flex-row-reverse",
            )}
          >
            <div
              className={cn(
                "flex items-center gap-2",
                isRTL && "flex-row-reverse",
              )}
            >
              {(() => {
                const GroupIcon = GROUP_ICONS[group.id];
                return GroupIcon ? (
                  <GroupIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
                ) : null;
              })()}
              <h2
                className={cn(
                  "text-xs font-bold uppercase text-gray-400 tracking-wider",
                  "group-hover:text-gray-600 transition-colors duration-200",
                  isRTL && "text-right",
                )}
              >
                {group.title}
              </h2>
            </div>
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 text-gray-400 transition-transform duration-200",
                "group-hover:text-gray-600",
                isOpen && "rotate-90",
                !isOpen && isRTL && "rotate-180",
              )}
            />
          </div>
        </CollapsibleTrigger>
        <div
          className={cn(
            "mt-1 h-px mx-3",
            isRTL
              ? "bg-gradient-to-l from-gray-200 to-transparent"
              : "bg-gradient-to-r from-gray-200 to-transparent",
          )}
        />
        <CollapsibleContent>
          <div className="mt-2 space-y-1">
            {/* Top-level items (e.g., Dashboard) */}
            {group.items.map(item => (
              <SidebarItem
                key={item.id}
                item={item}
                isActive={isRouteActive(item.to)}
                isCollapsed={false}
              />
            ))}

            {/* Subgroups */}
            {group.subgroups && (
              <div className="space-y-3 mt-2">
                {group.subgroups.map(subgroup => (
                  <SidebarSubgroupComponent
                    key={subgroup.id}
                    subgroup={subgroup}
                    isRouteActive={isRouteActive}
                    isRTL={isRTL}
                  />
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// Sidebar Group Component
interface SidebarGroupComponentProps {
  group: NavGroup;
  isCollapsed: boolean;
  currentPage: string;
}

const SidebarGroupComponent: React.FC<SidebarGroupComponentProps> = observer(
  ({ group, isCollapsed, currentPage }) => {
    const { i18n } = useTranslation();

    // Detect if current language is RTL (Arabic)
    const isRTL = i18n.language.startsWith("ar");

    // Collect all items including those in subgroups for route matching
    const allGroupItems = [
      ...group.items,
      ...(group.subgroups?.flatMap(sg => sg.items) || []),
    ];

    const isRouteActive = (path: string) => {
      if (path === "/") {
        return currentPage === "/";
      }
      if (currentPage === path) {
        return true;
      }
      const hasMoreSpecificMatch = allGroupItems.some(
        item =>
          item.to !== path &&
          item.to.startsWith(path) &&
          currentPage.startsWith(item.to),
      );
      if (hasMoreSpecificMatch) {
        return false;
      }
      return currentPage.startsWith(path + "/") || currentPage === path;
    };

    const totalItems = allGroupItems.length;
    if (totalItems === 0) {
      return null;
    }

    // Icons-only mode: render all items flat (no subgroup headers)
    if (isCollapsed) {
      return (
        <div className="space-y-1 mb-4">
          {allGroupItems.map(item => (
            <SidebarItem
              key={item.id}
              item={item}
              isActive={isRouteActive(item.to)}
              isCollapsed={isCollapsed}
            />
          ))}
        </div>
      );
    }

    // Groups with subgroups: render as a collapsible parent
    if (group.subgroups && group.subgroups.length > 0) {
      const hasActiveChild = allGroupItems.some(item => isRouteActive(item.to));

      return (
        <CollapsibleGroupContent
          group={group}
          allGroupItems={allGroupItems}
          defaultOpen={hasActiveChild}
          isRouteActive={isRouteActive}
          isRTL={isRTL}
        />
      );
    }

    // Default: flat group without subgroups
    const GroupIcon = GROUP_ICONS[group.id];
    return (
      <div className="mb-8">
        <div className="mb-4 px-3">
          <div
            className={cn(
              "flex items-center gap-2",
              isRTL && "flex-row-reverse",
            )}
          >
            {GroupIcon && (
              <GroupIcon className="h-4 w-4 text-gray-400" />
            )}
            <h2
              className={cn(
                "text-xs font-bold uppercase text-gray-400 tracking-wider",
                isRTL && "text-right",
              )}
            >
              {group.title}
            </h2>
          </div>
          <div
            className={cn(
              "mt-2 h-px",
              isRTL
                ? "bg-gradient-to-l from-gray-200 to-transparent"
                : "bg-gradient-to-r from-gray-200 to-transparent",
            )}
          />
        </div>
        <div className="space-y-2">
          {group.items.map(item => (
            <SidebarItem
              key={item.id}
              item={item}
              isActive={isRouteActive(item.to)}
              isCollapsed={isCollapsed}
            />
          ))}
        </div>
      </div>
    );
  },
);

/**
 * Pinned footer items rendered below the main nav group loop.
 *
 * These resources point outside the platform admin (documentation, support
 * contact) so they live in a separate region instead of polluting the
 * Settings group.
 */
interface FooterItem {
  id: string;
  to: string;
  icon: LucideIcon;
  i18nKey: string;
  fallback: string;
}

const SIDEBAR_FOOTER_ITEMS: FooterItem[] = [
  {
    id: "help-center",
    to: "/support-center",
    icon: BookOpen,
    i18nKey: "helpCenter.nav",
    fallback: "Help Center",
  },
  {
    id: "support-tickets",
    to: "/support-center/tickets",
    icon: LifeBuoy,
    i18nKey: "supportTickets.nav",
    fallback: "Support Tickets",
  },
];

interface SidebarFooterProps {
  isCollapsed: boolean;
  currentPage: string;
}

const SidebarFooter: React.FC<SidebarFooterProps> = ({
  isCollapsed,
  currentPage,
}) => {
  const { t, i18n } = useTranslation("modules");
  const { globalStore } = useStore(); // Add this
  const { isMobileView, setSidebarState } = globalStore; // Get mobile state
  const isRTL = i18n.language.startsWith("ar");

  const handleFooterClick = () => {
    // On mobile, close the sidebar after navigation
    if (isMobileView) {
      setTimeout(() => {
        setSidebarState(SidebarState.COLLAPSED);
      }, 100);
    }
  };

  return (
    <div
      className={cn(
        "flex-shrink-0 border-t border-slate-200 bg-white",
        isCollapsed ? "px-1.5 py-2" : "px-3 py-2",
      )}
      data-sidebar-region="footer"
    >
      <div className="space-y-0.5">
        {SIDEBAR_FOOTER_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive =
            currentPage === item.to || currentPage.startsWith(item.to + "/");
          const label = t(item.i18nKey, item.fallback);

          return (
            <Link
              key={item.id}
              to={item.to}
              title={isCollapsed ? label : undefined}
              onClick={handleFooterClick}
              className={cn(
                "flex items-center rounded-lg text-xs transition-colors duration-200",
                isCollapsed
                  ? "justify-center p-2 mx-auto w-[44px] h-[36px]"
                  : "px-2.5 py-1.5",
                isActive
                  ? "bg-slate-100 text-slate-900 font-medium"
                  : "text-slate-500 hover:text-slate-800 hover:bg-slate-50",
                isRTL && !isCollapsed && "flex-row-reverse text-right",
              )}
            >
              <Icon
                className={cn(
                  "flex-shrink-0",
                  isCollapsed ? "h-4 w-4" : "h-3.5 w-3.5",
                  !isCollapsed && (isRTL ? "ml-2" : "mr-2"),
                )}
              />
              {!isCollapsed && <span className="truncate">{label}</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// Main Sidebar Component
const Sidebar: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentPage = location.pathname;
  const { globalStore } = useStore();
  const { sidebarState, isMobileView, setSidebarState } = globalStore;
  const { noOrgSelected } = useAuth();

  // Get navigation groups from module registry
  const { isEnabled } = useFeatureFlagsContext();

  const navGroups = useNavigation({
    isEnabled: (flagName: string) => isEnabled(flagName),
  });
  // Detect if current language is RTL (Arabic)
  const isRTL = i18n.language.startsWith("ar");

  const isCollapsed = sidebarState === SidebarState.COLLAPSED;
  const isIconsOnly = sidebarState === SidebarState.ICONS_ONLY;
  const isExpanded = sidebarState === SidebarState.EXPANDED;

  // Debug logging
  console.log("Sidebar render:", { isMobileView, sidebarState, isCollapsed });

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      globalStore.handleResize();
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [globalStore]);

  // REMOVED: The auto-close on route change was causing the issue
  // The useEffect below was immediately closing the sidebar after opening
  /*
  useEffect(() => {
    if (isMobileView && !isCollapsed) {
      setSidebarState(SidebarState.COLLAPSED);
    }
  }, [location.pathname, isMobileView, isCollapsed, setSidebarState]);
  */

  // Close when clicking outside (mobile)
  useEffect(() => {
    if (!isMobileView || isCollapsed) return;

    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('[data-sidebar="main"]');
      if (sidebar && !sidebar.contains(event.target as Node)) {
        console.log("Clicked outside - closing sidebar");
        setSidebarState(SidebarState.COLLAPSED);
      }
    };

    // Small delay to avoid immediate closing
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileView, isCollapsed, setSidebarState]);

  if (noOrgSelected) {
    return null;
  }

  // Mobile overlay
  const mobileOverlay = isMobileView && !isCollapsed && (
    <div
      className="fixed inset-0 bg-black/50 z-40 lg:hidden"
      onClick={() => setSidebarState(SidebarState.COLLAPSED)}
    />
  );

  return (
    <>
      {mobileOverlay}

      {/* SIDEBAR */}
      <aside
        data-sidebar="main"
        dir={isRTL ? "rtl" : "ltr"}
        className={cn(
          // Base styles
          "fixed top-0 h-screen bg-white transition-all duration-300 ease-in-out",
          isRTL
            ? "border-l border-slate-200"
            : "border-r border-slate-200",

          // Desktop styles
          !isMobileView && [
            isCollapsed && "w-0 overflow-hidden",
            isIconsOnly && "w-16",
            isExpanded && "w-64",
            isRTL ? "right-0" : "left-0",
          ],

          // Mobile styles - ALWAYS width 64, use transform for show/hide
          isMobileView && [
            "w-64",
            "shadow-xl",
            isRTL ? "right-0" : "left-0",
            // IMPORTANT: Use transform to slide in/out
            isCollapsed
              ? isRTL
                ? "translate-x-full"
                : "-translate-x-full"
              : "translate-x-0",
          ],
        )}
        style={{
          zIndex: isMobileView && !isCollapsed ? 50 : 40,
        }}
      >
        <div className="relative flex h-full flex-col pt-16">
          {/* Desktop toggle button */}
          {!isMobileView && (
            <button
              onClick={() => globalStore.toggleSidebar()}
              className={cn(
                "absolute top-4 flex h-7 w-7 items-center justify-center rounded-full",
                "bg-white border border-slate-200 text-slate-500 shadow-sm",
                "transition-all duration-200 hover:bg-slate-50 hover:text-slate-700 hover:shadow z-50",
                isCollapsed
                  ? isRTL
                    ? "right-1/2 translate-x-1/2"
                    : "left-1/2 -translate-x-1/2"
                  : isRTL
                    ? "-left-3.5"
                    : "-right-3.5",
              )}
              title={isCollapsed ? t("expand-sidebar") : t("collapse-sidebar")}
            >
              {isCollapsed ? (
                isRTL ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )
              ) : isRTL ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Mobile header */}
          {isMobileView && !isCollapsed && (
            <div
              className="flex items-center justify-between p-4 border-b border-gray-200 absolute top-0 h-16 bg-white z-10"
              style={{ left: 0, right: 0 }}
            >
              <h1 className="text-xl font-bold text-gray-900">{t("botbat")}</h1>
              <button
                onClick={() => setSidebarState(SidebarState.COLLAPSED)}
                className="h-8 w-8 rounded-full p-0 text-gray-500 hover:text-gray-700"
                aria-label={t("close-sidebar", "Close sidebar")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Sidebar content */}
          <nav
            data-tour="sidebar-nav"
            className={cn(
              "flex-1 overflow-y-auto py-4",
              isIconsOnly && !isMobileView ? "px-1.5" : "px-3",
              isMobileView && !isCollapsed && "mt-16",
            )}
          >
            {navGroups.map(group => (
              <SidebarGroupComponent
                key={group.id}
                group={group}
                isCollapsed={isMobileView ? false : isIconsOnly}
                currentPage={currentPage}
              />
            ))}
          </nav>

          {/* Pinned footer */}
          <SidebarFooter
            isCollapsed={isMobileView ? false : isIconsOnly}
            currentPage={currentPage}
          />
        </div>
      </aside>
    </>
  );
});

export default Sidebar;
